import type { AlertColor, SxProps, Theme } from '@mui/material';
import { createContext, useCallback, useContext } from 'react';

export type ToastConfig = {
  vertical?: 'bottom' | 'top';
  horizontal?: 'center' | 'left' | 'right';
  sx?: SxProps<Theme>;
  snackbarSx?: SxProps<Theme>;
};

type Severity = {
  severity: AlertColor;
};

export interface ToastMessage {
  message: string;
  header?: string;
  severity: AlertColor;
  key: number;
  config?: ToastConfig;
}

export type ToastInput = string | { message: string; header?: string; config?: ToastConfig };

export const ToastContext = createContext<{
  addMessage: (message: ToastMessage, config?: ToastConfig) => void;
}>(null as never);

export const useToast = () => {
  const { addMessage } = useContext(ToastContext);

  const show = (message: string, options: ToastConfig & Severity, header?: string) => {
    const { severity, ...config } = options;
    addMessage({ message, header, severity, config, key: Date.now() });
  };

  const getToastConfig = useCallback(
    (options: { hasHeader?: boolean } & ToastConfig & Severity): ToastConfig & Severity => {
      const { hasHeader = false, snackbarSx = {}, sx = {}, ...rest } = options;
      return {
        ...rest,
        snackbarSx: {
          width: 'max-content',
          '.MuiAlert-icon': {
            alignItems: hasHeader ? 'flex-start' : 'center',
            padding: 0,
            margin: '0 12px 0 0',
            '& .MuiSvgIcon-root': {
              fontSize: '22px',
              padding: hasHeader ? '7px 0' : 0,
            },
          },
          '.MuiAlert-action': {
            alignItems: hasHeader ? 'flex-start' : 'center',
            padding: 0,
            margin: hasHeader ? '4px 0 0 16px' : '0 0 0 16px',
          },
          '.MuiIconButton-root': {
            width: '28px',
            height: '28px',
            padding: '4px',
            flexShrink: 0,
            '& .MuiSvgIcon-root': {
              fontSize: '20px',
              padding: 0,
            },
          },
          '.MuiAlert-message': {
            padding: '8px 0',
            '& .MuiAlertTitle-root': {
              margin: '0 0 4px 0',
              fontWeight: 600,
              fontSize: '16px',
              lineHeight: '26px',
              letterSpacing: '0px',
            },
          },
          ...snackbarSx,
        },
        sx: {
          // Alert content styling
          width: 'max-content',
          wordWrap: 'break-word',
          whiteSpace: 'normal',
          borderRadius: '8px',
          fontWeight: 350,
          fontSize: '16px',
          lineHeight: '26px',
          letterSpacing: '0px',
          ...sx,
        },
      };
    },
    [],
  );

  // Helper to normalize input
  const normalizeInput = (
    input: ToastInput,
  ): { message: string; header?: string; config?: ToastConfig } => {
    if (typeof input === 'string') {
      return { message: input };
    }
    return input;
  };

  return {
    show,
    info(input: ToastInput) {
      const { message, header, config } = normalizeInput(input);
      show(message, getToastConfig({ severity: 'info', hasHeader: !!header, ...config }), header);
    },
    success(input: ToastInput) {
      const { message, header, config } = normalizeInput(input);
      show(
        message,
        getToastConfig({ severity: 'success', hasHeader: !!header, ...config }),
        header,
      );
    },
    warning(input: ToastInput) {
      const { message, header, config } = normalizeInput(input);
      show(
        message,
        getToastConfig({ severity: 'warning', hasHeader: !!header, ...config }),
        header,
      );
    },
    error(input: ToastInput) {
      const { message, header, config } = normalizeInput(input);
      show(message, getToastConfig({ severity: 'error', hasHeader: !!header, ...config }), header);
    },
  };
};
